import { ALLOWED_FILE_TYPES, ALLOWED_MIME_TYPES } from '@/ui/constants/chat';

export const sortByUpdatedAtDesc = (a: { updatedAt: string }, b: { updatedAt: string }) =>
  new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();

export const filterFilesByType = (files: File[]): { valid: File[]; invalid: File[] } => {
  const valid: File[] = [];
  const invalid: File[] = [];

  files.forEach((file) => {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    const isValidType = ALLOWED_FILE_TYPES.includes(fileExtension) || ALLOWED_MIME_TYPES.includes(file.type);

    if (isValidType) {
      valid.push(file);
    } else {
      invalid.push(file);
    }
  });

  return { valid, invalid };
};

const MAX_NAMED_FILES = 3;

const joinList = (items: string[], conjunction: 'and' | 'or') =>
  items.length > 1 ? `${items.slice(0, -1).join(', ')} ${conjunction} ${items.at(-1)}` : (items[0] ?? '');

const ACCEPTED_FORMATS = joinList(
  ALLOWED_FILE_TYPES.map((extension) => extension.slice(1).toUpperCase()),
  'or',
);

// Past three names, two names and a count, so a long selection keeps the toast short.
const listFileNames = (fileNames: string[]) => {
  if (fileNames.length <= MAX_NAMED_FILES) return joinList(fileNames, 'and');
  const named = fileNames.slice(0, MAX_NAMED_FILES - 1);
  return joinList([...named, `${fileNames.length - named.length} more files`], 'and');
};

export const getUnsupportedFormatMessage = (fileNames: string[]) =>
  `${listFileNames(fileNames)} ${fileNames.length === 1 ? 'isn’t' : 'aren’t'} in a supported format. ` +
  `Try uploading a ${ACCEPTED_FORMATS} instead.`;

/** Toast for the files `filterFilesByType` rejected; it only says the upload failed when nothing got through. */
export const getUnsupportedFormatToast = (rejected: File[], hasAcceptedFiles: boolean) => {
  const count = rejected.length;
  return {
    summary: hasAcceptedFiles ? `${count} ${count === 1 ? 'file wasn’t' : 'files weren’t'} uploaded` : 'Upload failed',
    detail: getUnsupportedFormatMessage(rejected.map((file) => file.name)),
  };
};

type ApiErrorShape = { data?: { detail?: Array<{ msg?: string } | undefined> } };

export function getApiErrorDetailMessage(error: unknown, fallback: string): string {
  const fromApi = (error as ApiErrorShape | null)?.data?.detail?.[0]?.msg?.trim();
  if (fromApi) return fromApi;
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}
